import { Icon, TimelineItem } from '@ps/isp-web-component-library';
import { Ninety } from '@ps/isp-web-component-library/icons/Ninety';
import { TwoUpIcon } from '@ps/isp-web-component-library/icons/TwoUpIcon';
import { type MarketId, type SelectionId } from '@ps/isp-web-services-types';
import { getIsMatchOddsNinetyMarket, getIsTwoUpMarket } from '@ps/web-sports-utils';
import React from 'react';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import StarSub from '@swf/generated/sports-icons/starSub';
import { Icons, Selection } from '@swf/sports/components';
import FormattedDateTime from '@swf/sports/components/FormattedDateTime/FormattedDateTime';
import { useNormalisedEventBasedData } from '@swf/sports/hooks/useNormalisedEventBasedData';
import { getEventTypeIcon } from '@swf/sports/utils/getEventTypeIcon';
import { getHandicapSelectionValue } from '@swf/sports/utils/getHandicapSelectionValue';

import {
  TXTWCL_Match_Odds_90,
  TXTWCL_Match_Odds___2_Up,
  TXTWCL_Star_Sub_enabled_selection
} from './EventBasedPopularAccumulatorItem.messages.json';
import styles from './EventBasedPopularAccumulatorItem.scss';

interface Props {
  marketId: MarketId;
  selectionId: SelectionId;
}

const EventBasedPopularAccumulatorItem: React.FC<Props> = ({ marketId, selectionId }: Props) => {
  const { market, event } = useNormalisedEventBasedData({ marketId });
  const { formatMessage } = useRegulatoryIntl();

  const runner = market?.runners.find((runner) => runner.selectionId === selectionId);
  const isMatchOddsNinetyMarket = getIsMatchOddsNinetyMarket(market?.marketType);
  const isTwoUpMarket = getIsTwoUpMarket(market?.marketType);

  if (!market || !runner) return null;

  const { runnerName, handicap, resultType } = runner;
  const handicapValue = getHandicapSelectionValue({
    handicap,
    runnerResultType: resultType
  });

  return (
    <TimelineItem textWrap="balance" icon={Icons[getEventTypeIcon(event.eventTypeId)]}>
      <div className={styles.item}>
        <div className={styles.singleDetails}>
          <span>
            <strong>
              {runnerName}
              {handicapValue ? ` (${handicapValue})` : null}
            </strong>{' '}
            - {market.marketName}
            {market.isStarSubEnabled && (
              <Icon
                icon={StarSub}
                className={styles.marketIcon}
                label={formatMessage(TXTWCL_Star_Sub_enabled_selection)}
                size="xs"
              />
            )}
            {isMatchOddsNinetyMarket ? (
              <Icon
                icon={Ninety}
                className={styles.marketIcon}
                label={formatMessage(TXTWCL_Match_Odds_90)}
                size="xs"
              />
            ) : null}
            {isTwoUpMarket ? (
              <Icon
                icon={TwoUpIcon}
                className={styles.marketIcon}
                label={formatMessage(TXTWCL_Match_Odds___2_Up)}
                size="xs"
              />
            ) : null}
          </span>
          <span>{event.eventName}</span>
          <span>
            <FormattedDateTime date={event.eventStartTime} format="long" minimal />
          </span>
        </div>
        <span className={styles.selection}>
          <Selection
            competitionId={market.competitionId}
            eventId={market.eventId}
            eventTypeId={market.eventTypeId}
            handicap={runner.handicap}
            isStartingPriceMarket={market.isStartingPriceMarket}
            key={runner.selectionId}
            legTypes={market.legTypes}
            marketId={market.marketId}
            marketStatus={market.marketStatus}
            marketType={market.marketType}
            odds={runner.winRunnerOdds}
            runnerResultType={runner.resultType}
            runnerStatus={runner.runnerStatus}
            selectionId={runner.selectionId}
            startTime={market.marketTime}
          />
        </span>
      </div>
    </TimelineItem>
  );
};

export default EventBasedPopularAccumulatorItem;
